import type {
  ProfileViewModel,
  SubmitVouchModel,
  SupportInfo,
  WaitListRequest,
} from "@/lib/types";

export interface ServerResponse<T = unknown> {
  ApiHttpResponse: number;
  ApiMessages: string[];
  ApiResult: T;
  PublicMessage?: string;
}

export const sendTicketToSupport = async (
  supportInfo: SupportInfo,
): Promise<ServerResponse<[]>> => {
  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/Ticket/Create`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      body: JSON.stringify(supportInfo),
    },
  );

  return response.json();
};

export async function fetchVouchSession(
  vouchId: string,
): Promise<ServerResponse<ProfileViewModel>> {
  const baseUrl = `${import.meta.env.VITE_BASE_URL}/Vouch/Public`;
  const url = new URL(baseUrl);
  url.searchParams.append("VouchId", vouchId);

  const response = await fetch(url.toString(), {
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
  });

  return response.json();
}

export const getOtp = async (
  Phone: string,
  Purpose: "vouch" = "vouch",
): Promise<ServerResponse<[]>> => {
  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/OTP/SendByPhone`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      body: JSON.stringify({ Phone, Purpose }),
    },
  );

  return response.json();
};

export const submitVouch = async (
  payload: SubmitVouchModel,
): Promise<ServerResponse<[]>> => {
  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/Vouch/Submit`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      body: JSON.stringify(payload),
    },
  );

  return response.json();
};

export const joinWaitList = async (
  payload: WaitListRequest,
): Promise<ServerResponse<[]>> => {
  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/Waitlist/Join`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      body: JSON.stringify(payload),
    },
  );

  const body: ServerResponse<[]> = await response.json();

  // A non-2xx status is a failure even though fetch resolves — throw the
  // parsed body so the mutation lands in onError and never reports success.
  if (!response.ok) {
    throw body;
  }

  // Pin ApiHttpResponse to the true status so callers can distinguish
  // 200 (already on the list) from 201 (newly added).
  return { ...body, ApiHttpResponse: response.status };
};

export const unsubscribeContact = async (
  token: string,
): Promise<ServerResponse<{ Updated: number }>> => {
  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/Contact/Unsubscribe`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      body: JSON.stringify({ Token: token }),
    },
  );

  const body: ServerResponse<{ Updated: number }> = await response.json();

  if (!response.ok) {
    throw body;
  }

  return body;
};
